import React, { useMemo, useState } from 'react'
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { Badge, Tabs, Dropdown, Checkbox, Tooltip, Rate } from "antd";
import { EllipsisVertical, ThumbsUp, ChevronRight, X } from "lucide-react";
import TalentDisplayName from "../../components/TalentDisplayName";
import HideTalentPricingTooltip from "../clients/HideTalentPricingTooltip";
import ClientPreferredRateModal from "./ClientPreferredRateModal";
import { UserImageCircleNEW } from "../../components/UserImageCircle";
import { useAuth } from "../../providers/AuthProvider";
import { useTalent } from '../../providers/TalentProvider';
import TalentMatchDetails from "./TalentMatchDetails";
import CandidateHireConfidence from "./CandidateHireConfidence";
import CandidateRoleMatch from "./CandidateRoleMatch";
import useSharedFitTab from "./useSharedFitTab";
import TalentTileBadges from "./TalentTileBadges";
import TalentReviewModal from './TalentReviewModal';


const TalentTileRedesign = ({ children, ...props }) => {
    const {
        talent,
        matchingDisciplines,
        isDisabled: isTalentActionsDisabled,
        criteria,
        canFavorite,
        removeFromFavorites,
        addToFavorites,
        overrideClientPreferredRate,
    } = useTalent();
    const { auth } = useAuth();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab, canSeeHireConfidence] = useSharedFitTab();
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

    const profileLink = useMemo(() => {
        let params = new URLSearchParams(searchParams);
        return {
            pathname: `talent-profiles/${talent.id}`,
            search: params.size > 0 ? `?${params.toString()}` : undefined
        }
    }, [searchParams?.toString()])

    const showMatchDetails = () => {
        let matchCriteria = {};
        if (criteria) {
            matchCriteria = criteria;
        }
        return TB.event('Modal@show', {
            modalClass: 'match-details-modal',
            content: (
                <TalentMatchDetails
                    talent={talent}
                    isEngagementLead={false}
                    criteria={matchCriteria}
                />
            )
        })
    }

    const onTalentAction = async (request) => {
        let update;
        if (props.onRequest) {
            update = await props.onRequest(request);
        } else await request();
        if (update && props.onUpdated) {
            await props.onUpdated(update);
        }
    }

    if (props.placeholder) {
        return <TalentTileRedesignSkeleton>{children}</TalentTileRedesignSkeleton>;
    }

    return (
        <article
            className="bg-white border border-[#E2E3EA] shadow-sm hover:shadow-[0_2px_4px_rgba(14,14,18,0.05),0_16px_36px_rgba(14,14,18,0.12)] hover:-translate-y-px flex flex-col transition-all duration-200 flex-1 h-full rounded-2xl overflow-hidden group relative"
        >
            {/* HEADER ROW */}
            <div className="min-h-[34px] flex relative items-center justify-between py-2 px-3.5 text-xs font-semibold bg-gradient-to-b from-[#F4F5F9] to-[#EEF0F5] border-b border-[#EDEEF2]" />

            {/* ACTIONS ROW */}
            <ActionRow
                isDisabled={props.isDisabled || isTalentActionsDisabled}
                onTalentAction={onTalentAction}
                onRemoveFromPool={props.onRemoveFromPool}
                canRunBulkActions={props.canRunBulkActions}
                onAddToBulkActions={props.onAddToBulkActions}
                isBulkActionsSelected={props.isBulkActionsSelected}
                bulkActions={props.bulkActions}
            />

            {/* TALENT INFO ROW */}
            <div className="flex flex-col items-center text-center px-[22px] pb-3">
                <Link
                    to={profileLink}
                    className={[
                        'relative flex h-[128px] w-[128px] mb-2.5 shrink-0 items-center justify-center rounded-full',
                        'border border-white transition-shadow duration-200',
                        'shadow-[0_0_0_1px_#E2E3EA,0_6px_18px_rgba(0,0,0,0.09)]',
                        'hover:shadow-[0_0_0_2px_#5F3DB1,0_6px_14px_rgba(95,61,177,0.15)]',
                        '[&>div]:!h-full [&>div]:!w-full',
                        '[&_img]:!h-full [&_img]:!w-full [&_img]:!max-w-none [&_img]:!object-cover [&_img]:!rounded-full',
                    ].join(' ')}
                    onClick={e => e.stopPropagation()}
                >
                    <UserImageCircleNEW
                        user={talent}
                        className="text-4xl"
                    />
                </Link>

                <TalentDisplayName talent={talent}>
                    <p className="mb-0 max-w-full line-clamp-1 text-[16.5px] font-bold tracking-[-0.3px] text-[#0E0E14] whitespace-nowrap text-ellipsis transition-colors hover:text-primary">
                        {talent.firstNamePlusInitial()}
                        {talent.is_new && <Badge count="NEW" className="ml-2" status="error" />}
                    </p>
                </TalentDisplayName>

                <p className="mt-[5px] mb-0 max-w-full min-h-[17px] line-clamp-1 text-[12.5px] font-medium leading-[1.3] text-[#595D68]">
                    {talent.fullJobTitle?.()}
                </p>

                <div className="mt-[9px] flex min-h-[15px] items-center">
                    {talent.location && (
                        <p className="mb-0 inline-flex items-center gap-1 text-[11.5px] font-medium leading-none text-[#8B8F9A]">
                            <i className="fa fa-map-marker-alt" />
                            {talent.location.city}{!!talent.location.state && `, ${talent.location.state}`}
                        </p>
                    )}
                </div>

                {TB.first(matchingDisciplines) && (
                    <div className="mt-2 flex min-h-[24px] max-w-full items-center">
                        <p className="mb-0 inline-flex max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-full border-[1.5px] border-primary bg-white py-[3px] px-[11px] text-[11.5px] font-semibold leading-none text-primary">
                            {TB.first(matchingDisciplines).name}
                        </p>
                    </div>
                )}

                {props.includeRating && (
                    <button
                        type="button"
                        className="mt-2 flex flex-col hover:underline items-center cursor-pointer rating-button"
                        onClick={() => setIsReviewModalOpen(!isReviewModalOpen)}
                    >
                        <Rate disabled allowHalf value={talent.avg_review_rating || 0} />
                        <p className="text-primary font-semibold text-lg">
                            {TB.pluralize('Review', talent.reviews_count, true)}
                        </p>
                    </button>
                )}
            </div>

            {/* SUMMARY ROW */}
            <div className="text-center px-[22px] pb-2.5">
                <p className="mb-0 h-[38px] line-clamp-2 text-[12.5px] leading-[1.5] text-[#595D68]">
                    {talent.fullBio() || ''}
                </p>

                <Link
                    to={profileLink}
                    className="mt-1.5 inline-block text-[11.5px] font-semibold text-primary no-underline hover:underline hover:underline-offset-[3px]"
                    onClick={e => e.stopPropagation()}
                >
                    View Profile →
                </Link>
            </div>

            {/* MATCH ROW */}
            {talent?.hasMatching() && (
                <div className="flex flex-col pt-2 px-[22px] pb-6 border-t border-[#EDEEF2]">
                    <Tabs
                        activeKey={activeTab}
                        centered
                        className={[
                            '[&_.ant-tabs-nav:before]:!border-none',
                            '[&_.ant-tabs-tab]:!py-0.5 [&_.ant-tabs-tab]:!px-0',
                            '[&_.ant-tabs-tab+.ant-tabs-tab]:!ml-4',
                            '[&_.ant-tabs-tab]:!text-xxs [&_.ant-tabs-tab]:!font-semibold',
                            '[&_.ant-tabs-tab]:!uppercase [&_.ant-tabs-tab]:!tracking-[0.09em]',
                            '[&_.ant-tabs-tab_.ant-tabs-tab-btn]:!text-[#8B8F9A]',
                            '[&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-[#0E0E14]',
                            // A client sees one tab, so none of the tab chrome
                            // applies: no underline marking a selection that
                            // can't change, and no gap below the nav, leaving
                            // the label to read as a heading over the panel.
                            //
                            // Emitted as either/or rather than layered
                            // overrides — `mb-0` and `mb-[11px]` tie on
                            // specificity, so both present would leave source
                            // order to decide.
                            canSeeHireConfidence
                                ? '[&_.ant-tabs-nav]:!mb-[11px] [&_.ant-tabs-ink-bar]:!h-px [&_.ant-tabs-ink-bar]:!bg-[#0E0E14]'
                                : '[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-ink-bar]:!hidden',
                            '[&_.ant-tabs-content]:!transition-[height] [&_.ant-tabs-content]:!duration-300 [&_.ant-tabs-content]:!ease-out',
                            '[&_.ant-tabs-tabpane]:!h-full',
                            // Each tab is locked to the tallest thing it can
                            // hold, so tiles stay aligned whether or not a
                            // talent has badges or a beta row. Role Match runs
                            // taller for staff, who also get the AI-Weighted
                            // Match row; add ~25px to both if Profile Skills &
                            // Experience is uncommented in CandidateRoleMatch.
                            activeTab === 'hire-confidence'
                                ? '[&_.ant-tabs-content]:!h-[206px]'
                                : canSeeHireConfidence
                                    ? '[&_.ant-tabs-content]:!h-[200px]'
                                    : '[&_.ant-tabs-content]:!h-[140px]',
                            // Nothing to switch to, so nothing to click.
                            canSeeHireConfidence ? '' : '[&_.ant-tabs-nav]:!pointer-events-none',
                        ].join(' ')}
                        onChange={setActiveTab}
                        items={[
                            {
                                // Staff see the blended Role Match as a beta
                                // row inside this tab, so the ring here is
                                // named for what it actually shows: the
                                // profile-side match.
                                label: canSeeHireConfidence ? 'Profile Match' : 'Role Match',
                                key: 'role-match',
                                children: (
                                    <FitPanel>
                                        <CandidateRoleMatch
                                            candidate={talent}
                                            onShowMatchDetails={showMatchDetails}
                                            isActive={activeTab === 'role-match'}
                                        />
                                    </FitPanel>
                                ),
                            },
                            ...(canSeeHireConfidence ? [{
                                label: 'Hire Confidence',
                                key: 'hire-confidence',
                                // Browsing talent is outside any position, so
                                // there is no proposal to wait on — the tab
                                // shows platform track record instead of a
                                // padlock that can never be opened here.
                                //
                                // `isActive` replays the ring and bar fills on
                                // each visit — antd keeps the hidden pane
                                // mounted, so without it they animate once and
                                // never again.
                                children: (
                                    <FitPanel>
                                        <CandidateHireConfidence
                                            candidate={talent}
                                            profileLink={profileLink}
                                            criteria={criteria}
                                            supportsProposals={false}
                                            isActive={activeTab === 'hire-confidence'}
                                        />
                                    </FitPanel>
                                ),
                            }] : []),
                        ]}
                    />
                </div>
            )}


            {/* RATE ROW */}
            <div className="bg-[#F5F5F7] pt-3 px-[22px] pb-3.5 flex-1 flex flex-col items-center gap-2">
                <RateRow talent={talent} />

                {children}
            </div>


            <TalentReviewModal
                isOpen={isReviewModalOpen}
                onClose={() => setIsReviewModalOpen(false)}
                talent={talent}
                onUpdateTalent={props.onUpdated}
            />
        </article>
    )
}

export default TalentTileRedesign;


/**
 * Fills the height `.ant-tabs-content` sets for the active tab.
 *
 * `overflow-hidden` is the guarantee: without it, a panel whose content runs
 * long spills past the locked height and the tile beside it no longer lines
 * up. Clipping keeps every tile in a row the same height, whatever it holds.
 */
const FitPanel = ({ children }) => (
    <div className="flex flex-col h-full overflow-hidden">
        {children}
    </div>
);


// ==================== Action Row ====================

const ActionRow = ({ onTalentAction, isDisabled, onRemoveFromPool, canRunBulkActions, onAddToBulkActions, isBulkActionsSelected, bulkActions = [] }) => {
    const { auth } = useAuth();
    const {
        talent,
        canFavorite,
        removeFromFavorites,
        addToFavorites,
        overrideClientPreferredRate,
    } = useTalent();
    const [isClientPreferredRateOpen, setIsClientPreferredRateOpen] = useState(false);

    const dropdownItems = [
        ...(talent?.email ? [{
            key: 'email',
            icon: <i className="fas fa-envelope" />,
            label: (
                <a href={`mailto:${talent.email}?subject=Message from Wripple Client Lead regarding ${talent.job_title}`}>
                    {talent.email}
                </a>
            ),
        }] : []),
        ...(talent?.phone ? [{
            key: 'call',
            icon: <i className="fas fa-phone mb-[3px]" />,
            label: <a href={`tel:${talent.phone}`}>{talent.phone}</a>,
        }] : []),
        {
            key: 'set-rate',
            icon: <i className="fas fa-dollar-sign mb-[3px]" />,
            label: 'Set Client Preferred Rate',
            onClick: () => setIsClientPreferredRateOpen(true),
            disabled: isDisabled
        },
        ...(talent?.client_override_hourly_rate ? [{
            key: 'delete-rate',
            icon: <i className="fas fa-eraser mb-[3px]" />,
            label: 'Remove Client Preferred Rate',
            onClick: async () => {
                const confirm = await TB.alert("warning", "Remove Client Preferred Rate",
                    "Are you sure you want to remove this talent's preferred rate for this client?",
                    [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
                );
                if (confirm) {
                    return overrideClientPreferredRate(false);
                }
            },
            disabled: isDisabled
        }] : []),
    ];

    return (
        <div className="flex relative justify-between items-start pt-2 px-2.5 min-h-[34px]">
            <div className="flex gap-1.5 flex-1 justify-start items-center">
                {!bulkActions.length && onRemoveFromPool && (
                    <IconButton
                        title="Remove from pool"
                        className="hover:!bg-red-50 hover:!text-red-500"
                        disabled={isDisabled}
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemoveFromPool();
                        }}
                    >
                        <X className="h-3.5 w-3.5" />
                    </IconButton>
                )}

                {canRunBulkActions && onAddToBulkActions && (
                    <div className={`ml-1.5 transition group-hover:visible ${isBulkActionsSelected ? 'visible' : 'invisible'}`} onClick={e => e.stopPropagation()}>
                        <Checkbox
                            checked={isBulkActionsSelected}
                            onChange={() => onAddToBulkActions()}
                            className="[&_.ant-checkbox-inner]:scale-150 [&_.ant-checkbox]:h-6 [&_.ant-checkbox]:w-6"
                        />
                    </div>
                )}
            </div>

            {talent.supplier_client?.id ? (
                <Link
                    to={`/agency-profiles/${talent.supplier_client.id}`}
                    onClick={e => e.stopPropagation()}
                    className="text-[9.5px] font-semibold self-center uppercase tracking-[0.1em] flex gap-1.5 items-center py-0.5 px-[9px] rounded-full border border-[#EFE9F5] text-[#A49CB8] transition-colors hover:border-primary hover:text-primary"
                >
                    Agency: <img src={talent.supplier_client.logo_path} className="h-5 img-fluid max-w-full" />
                </Link>
            ) : (
                <p className="text-[9.5px] font-semibold uppercase self-center mb-0 py-0.5 px-[9px] tracking-[0.1em] rounded-full border border-[#EFE9F5] text-[#A49CB8]">
                    Freelancer
                </p>
            )}

            <div className={`flex gap-1.5 flex-1 justify-end items-center ${!!bulkActions?.length && 'invisible'}`}>
                {canFavorite && (
                    <Tooltip title={talent.is_favorite ? 'Remove from saved' : 'Save as Favorite'}>
                        <IconButton
                            disabled={isDisabled}
                            onClick={(e) => {
                                e.stopPropagation();
                                onTalentAction(talent.is_favorite ? removeFromFavorites : addToFavorites);
                            }}
                            className={talent.is_favorite ? '!bg-[#ECFDF3] !text-[#4ADE80]' : ''}
                        >
                            <i className={`fa-heart text-xs ${talent.is_favorite ? 'fas text-[#4ADE80]' : 'far'}`} />
                        </IconButton>
                    </Tooltip>
                )}

                {!bulkActions?.length && (auth.isClientManager() || auth.isClientConsultant()) && (
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        menu={{ items: dropdownItems }}
                    >
                        <IconButton onClick={e => e.stopPropagation()}>
                            <EllipsisVertical className="h-3.5 w-3.5" />
                        </IconButton>
                    </Dropdown>
                )}
            </div>

            {!bulkActions?.length && (auth.isClientManager() || auth.isClientConsultant()) && (
                <ClientPreferredRateModal
                    isDisabled={isDisabled}
                    onClose={setIsClientPreferredRateOpen}
                    isOpen={isClientPreferredRateOpen}
                />
            )}

            {/* <TalentTileBadges /> */}
        </div>
    )
}


// ==================== Shared Sub-Components ====================

const IconButton = ({ className, children, ...props }) => (
    <button
        // Borderless at rest: three outlined boxes across the top competed with
        // the card's own edge. The hover fill still gives the target a visible
        // boundary at the moment it matters.
        className={`h-[30px] w-[30px] rounded-lg bg-transparent text-[#595D68] flex items-center disabled:pointer-events-none disabled:opacity-60 justify-center border border-transparent transition-all duration-150 hover:bg-[#F1F1F4] hover:text-[#1F2029] hover:-translate-y-[1px] ${className || ''}`}
        {...props}
    >
        {children}
    </button>
);

const RateLabel = ({ children, isProposed }) => (
    <p className={TB.mapClass("mb-0 text-xxs font-bold uppercase tracking-[0.12em]", {
        'text-primary': isProposed,
        'text-[#595D68]': !isProposed,
    })}>
        {children}
    </p>
);

const RateValue = ({ children, isProposed }) => (
    <span className={TB.mapClass("text-[26px] font-extrabold tabular-nums tracking-[-0.6px] leading-none", {
        'text-[#39178B]': isProposed,
        'text-[#0E0E14]': !isProposed,
    })}>
        {children}
    </span>
);

const RatePer = ({ children }) => (
    <span className="ml-px text-sm font-semibold text-[#595D68]">{children}</span>
);

const isZeroAmount = (value) => {
    if (value === null || !TB.isDefined(value)) return true;
    const numeric = String(value).replace(/[^0-9.]/g, '');
    return !numeric.length || Number(numeric) === 0;
};

const RateRow = ({ talent }) => {
    const { auth } = useAuth();

    const label = talent.client_override_hourly_rate ? 'Preferred Rate' : 'Standard Rate';
    const rate = talent.rangedPlatformRates?.() || talent.standardPlatformRate?.();

    return (
        <div className="flex flex-col items-center justify-center gap-[3px] w-full min-h-[52px]">
            <RateLabel>{label}</RateLabel>
            {!auth.canViewTalentPricing() ? (
                <span className="text-lg font-bold"><HideTalentPricingTooltip /></span>
            ) : isZeroAmount(rate) ? (
                <span className="text-[26px] font-extrabold italic leading-none text-[#8B8F9A]">Pending</span>
            ) : (
                <RateValue>
                    {rate}
                    <RatePer>/hr</RatePer>
                </RateValue>
            )}
        </div>
    );
}


// ==================== Skeleton ====================

const Bar = ({ className, radius = 'rounded' }) => (
    <div className={`skeleton ${radius} ${className || ''}`} />
);

const TalentTileRedesignSkeleton = ({ children }) => (
    <article className="bg-white border border-[#E2E3EA] shadow-sm flex flex-col flex-1 h-full rounded-2xl overflow-hidden">
        <div className="min-h-[34px] bg-gradient-to-b from-[#F4F5F9] to-[#EEF0F5] border-b border-[#EDEEF2]" />

        <div className="flex justify-between items-start pt-2 px-2.5 min-h-[34px]">
            <div className="flex-1" />
            <Bar radius="rounded-full" className="h-[18px] w-[86px] self-center" />
            <div className="flex justify-end flex-1 gap-1.5">
                <Bar radius="rounded-lg" className="h-[30px] w-[30px]" />
                <Bar radius="rounded-lg" className="h-[30px] w-[30px]" />
            </div>
        </div>

        <div className="flex flex-col items-center px-[22px] pb-3">
            <Bar radius="rounded-full" className="h-[128px] w-[128px] mb-2.5" />
            <Bar className="h-[17px] w-1/2 mt-[3px] mb-[6px]" />
            <Bar className="h-3 w-3/4 mt-[7px]" />
            <Bar className="h-3 w-2/5 mt-[11px]" />
            <Bar radius="rounded-full" className="h-[25px] w-3/5 mt-2" />
        </div>

        <div className="flex flex-col items-center px-[22px] pb-5">
            <div className="h-[38px] w-full flex flex-col items-center gap-1.5">
                <Bar className="h-3 w-full" />
                <Bar className="h-3 w-4/5" />
            </div>
            <Bar className="h-3 w-1/4 mt-1.5" />
        </div>

        <div className="flex flex-col pt-2 px-[22px] pb-6 border-t border-[#EDEEF2]">
            <div className="flex justify-center gap-4 mb-[11px]">
                <Bar className="h-4 w-[72px]" />
                <Bar className="h-4 w-[92px]" />
            </div>
            <div className="flex flex-col items-center justify-center min-h-[170px] pt-3 gap-[5px]">
                <Bar radius="rounded-full" className="h-[72px] w-[72px]" />
                <Bar className="h-[17px] w-2/5 mt-1" />
                <Bar className="h-3.5 w-3/4 mt-1" />
                <Bar className="h-3.5 w-1/2" />
                <Bar className="h-3.5 w-[128px] mt-3" />
            </div>
        </div>

        <div className="bg-[#F5F5F7] pt-3 px-[22px] pb-3.5 flex-1 flex flex-col items-center gap-2">
            <div className="flex flex-col items-center justify-center gap-[3px] w-full min-h-[52px]">
                <Bar className="h-2.5 w-1/3" />
                <Bar className="h-[26px] w-2/5 mt-1" />
            </div>
            {children}
        </div>
    </article>
);

const InvitedTalentTileRedesign = ({ children }) => {
    const { talent, isDisabled } = useTalent();

    return (
        <article
            className="bg-white border border-[#E2E3EA] shadow-sm hover:shadow-[0_2px_4px_rgba(14,14,18,0.05),0_16px_36px_rgba(14,14,18,0.12)] hover:-translate-y-px flex flex-col transition-all duration-200 flex-1 h-full rounded-2xl overflow-hidden group relative"
        >
            {/* HEADER ROW */}
            <div className="min-h-[34px] flex relative items-center justify-between py-2 px-3.5 text-xs font-semibold bg-gradient-to-b from-[#F4F5F9] to-[#EEF0F5] border-b border-[#EDEEF2]">
                <p>Invited: {TB.moment(talent.invited_at).isValid() ? TB.moment(talent.invited_at).format('ll') : 'N/A'}</p>
            </div>

            <div className="flex flex-col items-center text-center px-[22px] pb-3 pt-4">
                <div className="h-[128px] w-[128px] mb-2.5 shrink-0">
                    <UserImageCircleNEW
                        user={talent}
                        className="text-4xl w-full h-full"
                    />
                </div>

                <TalentDisplayName talent={talent}>
                    <p className="mb-0 max-w-full line-clamp-1 text-[16.5px] font-bold tracking-[-0.3px] text-[#0E0E14] whitespace-nowrap text-ellipsis transition-colors hover:text-primary">
                        {talent.firstNamePlusInitial()}
                        {talent.is_new && <Badge count="NEW" className="ml-2" status="error" />}
                    </p>
                </TalentDisplayName>

                <p className="mt-[5px] text-wrippleLightDarkest mb-0 max-w-full min-h-[17px] line-clamp-1 text-[12.5px] font-medium leading-[1.3] text[#595D68]">
                    {talent.email}
                </p>

                <p className="text-xl py-5 text-center text-wrippleLightDarkest">Not Yet Rated</p>

                <p className="text-xs text-center pb-1.5">
                    This person has not yet added a Profile. They can add one once accepting your invitation.
                </p>
            </div>

            <div className="bg-[#F5F5F7] pt-3 px-[22px] pb-3.5 flex-1 flex flex-col items-center gap-2">
                <div className="flex flex-col items-center justify-center gap-[3px] w-full min-h-[52px]">
                    <RateLabel>Rate Currently Unavailable</RateLabel>
                    <span className="text-[26px] pt-2 font-extrabold italic leading-none text-[#8B8F9A]">Pending</span>
                </div>

                {children}
            </div>

        </article>
    )
}

export { TalentTileRedesignSkeleton, InvitedTalentTileRedesign };
